import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";

// 2026-10-21: one place for payments: revenue summary, by plan, the list with a filter.
const STATUS_LABELS: Record<string, string> = {
  PENDING: "Ожидает оплаты",
  PAID: "Оплачен",
  FAILED: "Неуспешен",
  CANCELLED: "Отменён",
  REFUNDED: "Возврат",
  ERROR: "Ошибка",
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: "#f59e0b",
  PAID: "#10b981",
  FAILED: "#ef4444",
  CANCELLED: "#9ca3af",
  REFUNDED: "#3b82f6",
  ERROR: "#ef4444",
};

const FILTERS = [
  { key: "all", title: "Все" },
  { key: "paid", title: "Оплачены" },
  { key: "unpaid", title: "Без оплаты" },
];

function rub(v: number) {
  return `${Math.round(v).toLocaleString("ru-RU")} ₽`;
}

export default function AdminPayments() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<any[]>([]);
  const [sum, setSum] = useState<any>(null);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    api.get("/admin/payments-summary").then((r: any) => setSum(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const f = filter !== "all" ? `&status=${filter}` : "";
    api.get(`/admin/payments?page=${page}&page_size=20${f}`).then((r: any) => {
      setPayments(r.data.items);
      setTotal(r.data.total);
    }).finally(() => setLoading(false));
  }, [page, filter]);

  const statusLabel = (s: string) => STATUS_LABELS[s] || s;
  const statusColor = (s: string) => STATUS_COLORS[s] || "#9ca3af";

  return (
    <div style={styles.container}>
      <button style={styles.backBtn} onClick={() => navigate("/admin")}>← Админ-панель</button>
      <h1 style={styles.title}>Платежи</h1>

      {sum && (
        <>
          <div style={styles.big}>
            <div style={styles.bigValue}>{rub(sum.all.sum)}</div>
            <div style={styles.bigTitle}>выручка за всё время · оплат: {sum.all.count} · плательщиков: {sum.payers}</div>
          </div>
          <div style={styles.grid}>
            <div style={styles.tile}>
              <div style={styles.tileValue}>{rub(sum.month.sum)}</div>
              <div style={styles.tileTitle}>этот месяц · оплат: {sum.month.count}</div>
            </div>
            <div style={styles.tile}>
              <div style={styles.tileValue}>{rub(sum.today.sum)}</div>
              <div style={styles.tileTitle}>сегодня · оплат: {sum.today.count}</div>
            </div>
          </div>
          <div style={styles.hintLine}>
            Начали оплату и не завершили: {sum.unpaid}{sum.refunded ? ` · возвратов: ${sum.refunded}` : ""}
          </div>
          {sum.by_plan.length > 0 && (
            <div style={styles.planList}>
              {sum.by_plan.map((p: any) => (
                <div key={p.plan} style={styles.planRow}>
                  <span style={styles.planName}>{p.plan}</span>
                  <span style={styles.hint}>оплат: {p.count}</span>
                  <span style={styles.amount}>{rub(p.sum)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div style={styles.chips}>
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => { setPage(1); setFilter(f.key); }} style={{ ...styles.chip, ...(f.key === filter ? styles.chipOn : {}) }}>{f.title}</button>
        ))}
      </div>

      {loading ? (
        <div style={styles.center}><div style={styles.spinner} /></div>
      ) : payments.length === 0 ? (
        <div style={styles.hint}>Платежей нет</div>
      ) : (
        <div style={styles.list}>
          {payments.map((p) => (
            <div key={p.id} style={styles.card} onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}>
              <div style={styles.cardTop}>
                <div>
                  <div style={styles.planName}>{p.plan_name}</div>
                  <div style={styles.hint}>{p.user_username ? `@${p.user_username}` : `TG: ${p.user_telegram_id}`}</div>
                  <div style={styles.hint}>{new Date(p.created_at).toLocaleString("ru-RU")}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={styles.amount}>{(p.total_amount / 100).toLocaleString("ru-RU")} ₽</div>
                  <span style={{ ...styles.statusBadge, backgroundColor: statusColor(p.status) }}>{statusLabel(p.status)}</span>
                </div>
              </div>
              {expandedId === p.id && (
                <div style={styles.details}>
                  <div style={styles.detailRow}>ID платежа: {p.id}</div>
                  <div style={styles.detailRow}>ID пользователя: {p.user_id}</div>
                  <div style={styles.detailRow}>Telegram ID: {p.user_telegram_id}</div>
                  <div style={styles.detailRow}>Тариф: {p.plan_name}</div>
                  <div style={styles.detailRow}>Сумма: {(p.total_amount / 100).toLocaleString("ru-RU")} {p.currency}</div>
                  <div style={styles.detailRow}>Статус: {statusLabel(p.status)}</div>
                  <div style={styles.detailRow}>Создан: {new Date(p.created_at).toLocaleString("ru-RU")}</div>
                  <div style={styles.detailRow}>Оплачен: {p.paid_at ? new Date(p.paid_at).toLocaleString("ru-RU") : "—"}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={styles.pagination}>
        <button style={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>← Назад</button>
        <span style={styles.pageInfo}>{page} / {Math.max(1, Math.ceil(total / 20))}</span>
        <button style={styles.pageBtn} disabled={page * 20 >= total} onClick={() => setPage(page + 1)}>Вперёд →</button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: "16px", maxWidth: "480px", margin: "0 auto" },
  backBtn: { background: "none", border: "none", color: "var(--tg-theme-link-color, #2481cc)", fontSize: "14px", cursor: "pointer", padding: 0, marginBottom: "12px" },
  title: { fontSize: "22px", fontWeight: 700, margin: "0 0 12px 0", color: "var(--tg-theme-text-color, #000)" },
  big: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "16px", padding: "16px", textAlign: "center", marginBottom: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  bigValue: { fontSize: "30px", fontWeight: 700, color: "var(--tg-theme-text-color, #000)" },
  bigTitle: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)", marginTop: "4px" },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  tile: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "16px", padding: "14px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  tileValue: { fontSize: "20px", fontWeight: 700, color: "var(--tg-theme-text-color, #000)" },
  tileTitle: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)", marginTop: "4px" },
  hintLine: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)", margin: "10px 0" },
  planList: { display: "flex", flexDirection: "column", gap: "6px", marginBottom: "14px" },
  planRow: { display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "12px", padding: "10px 12px" },
  chips: { display: "flex", gap: "6px", margin: "6px 0 12px 0" },
  chip: { flex: 1, padding: "8px 0", borderRadius: "10px", border: "none", fontSize: "13px", cursor: "pointer", backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", color: "var(--tg-theme-text-color, #000)" },
  chipOn: { backgroundColor: "var(--tg-theme-button-color, #2481cc)", color: "var(--tg-theme-button-text-color, #fff)" },
  center: { display: "flex", justifyContent: "center", padding: "40px 0" },
  spinner: { width: "28px", height: "28px", border: "3px solid #e5e7eb", borderTop: "3px solid #3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" },
  hint: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)" },
  list: { display: "flex", flexDirection: "column", gap: "10px" },
  card: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "14px", padding: "12px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", cursor: "pointer" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  planName: { fontSize: "14px", fontWeight: 600, color: "var(--tg-theme-text-color, #000)" },
  amount: { fontSize: "14px", fontWeight: 700, color: "var(--tg-theme-text-color, #000)" },
  statusBadge: { display: "inline-block", marginTop: "4px", fontSize: "11px", color: "#fff", padding: "2px 8px", borderRadius: "10px" },
  details: { marginTop: "10px", paddingTop: "10px", borderTop: "1px solid var(--tg-theme-hint-color, #e0e0e0)" },
  detailRow: { fontSize: "12px", color: "var(--tg-theme-text-color, #000)", marginBottom: "4px" },
  pagination: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px", fontSize: "13px" },
  pageBtn: { background: "none", border: "none", color: "var(--tg-theme-link-color, #2481cc)", cursor: "pointer", fontSize: "13px" },
  pageInfo: { color: "var(--tg-theme-hint-color, #666)" },
};
